/* eslint-disable import/no-unresolved */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import Decimal from 'decimal.js';
import {
  CFD,
  ETF,
  FX,
  MARGIN_TOOLTIP,
  RECOMMENDED_MARGIN_LABEL,
  REQUIRED_MARGIN_LABEL,
  REQUIRED_MARGIN_TOOLTIP_CONFIRM,
} from '../../constants';
import { useTotalMarginForModal, useSummaryInfoByServiceId } from '.';
import { useSimulationChartDataLab, useSimulationChartDataSelection } from '../../hooks/select';
import { useCalculatingMarginByStrategyList } from '../../hooks';
import { getServiceId } from '../../utils';

const MIN_COUNT_VALUE = 1;
const INPUT_NAME = 'count';

const useAutoSelectRunNowConfirmation = ({ isOpen, onClose, data }) => {
  const { sets, callback, isLab } = data;

  const [countValue, setCount] = useState(MIN_COUNT_VALUE);
  const [errorMessages, changeErrorMessages] = useState([]);
  const hasSubmittedRef = useRef(false);

  const isDisableConfirmButton = useMemo(() => Boolean(errorMessages.length), [errorMessages]);

  const closeModalAndRefreshState = useCallback(() => {
    onClose();
    setCount(sets ?? MIN_COUNT_VALUE);
  }, [onClose, sets]);

  const onConfirm = useCallback(() => {
    if (errorMessages.length || hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    closeModalAndRefreshState();
    if (callback) {
      callback(countValue);
    }
  }, [callback, countValue, closeModalAndRefreshState, errorMessages]);

  const instrumentList = useSelector((state) => state.settings.instrumentList);

  const termId = useSelector((state) => state.autoSelect.selectedTermId);
  const selectStrategyList = useSelector((state) => state.autoSelect.selectionModalInfo[termId]?.strategyList);
  const labStrategyList = useSelector((state) => state.labs.publishedLabs.simulation.result.strategyList) || [];

  const selectionSimulationData = useSimulationChartDataSelection(countValue);
  const { simulationData } = useSimulationChartDataLab(countValue);
  const labMarginRecommended = simulationData?.marginRecommended ?? {};
  const {
    marginRecommended: selectionMarginRecommended,
    marginRecommendedFX,
    marginRecommendedETF,
    marginRecommendedCFD,
  } = selectionSimulationData.simulationData ?? {};

  let strategyList;
  let marginRecommended;
  if (isLab) {
    strategyList = labStrategyList.map((strategy) => {
      const { strategySets, ...strategyDetail } = strategy;
      const { instrumentId } = strategyDetail;
      const serviceId = getServiceId(instrumentId, instrumentList);
      return {
        strategySets,
        strategyDetail: {
          serviceId,
          ...strategyDetail,
        },
      };
    });
    marginRecommended = labMarginRecommended;
  } else {
    strategyList = selectStrategyList;
    marginRecommended = selectionMarginRecommended;
  }

  const totalMarginRequired = useCalculatingMarginByStrategyList(strategyList, countValue);
  const totalMarginRecommended = useTotalMarginForModal(isOpen, marginRecommended, countValue);
  const totalMarginRecommendedFX = useTotalMarginForModal(isOpen, marginRecommendedFX, countValue);
  const totalMarginRecommendedETF = useTotalMarginForModal(isOpen, marginRecommendedETF, countValue);
  const totalMarginRecommendedCFD = useTotalMarginForModal(isOpen, marginRecommendedCFD, countValue);

  const strategyListFx = strategyList?.filter((x) => x.strategyDetail.serviceId === FX) ?? [];
  let totalMarginRequiredFX = useCalculatingMarginByStrategyList(strategyListFx, countValue);
  if (!strategyListFx.length) {
    totalMarginRequiredFX = null;
  }
  const strategyListEtf = strategyList?.filter((x) => x.strategyDetail.serviceId === ETF) ?? [];
  let totalMarginRequiredETF = useCalculatingMarginByStrategyList(strategyListEtf, countValue);
  if (!strategyListEtf.length) {
    totalMarginRequiredETF = null;
  }
  const strategyListCfd = strategyList?.filter((x) => x.strategyDetail.serviceId === CFD) ?? [];
  let totalMarginRequiredCFD = useCalculatingMarginByStrategyList(strategyListCfd, countValue);
  if (!strategyListCfd.length) {
    totalMarginRequiredCFD = null;
  }

  const minMarginRequired = useCalculatingMarginByStrategyList(strategyList, MIN_COUNT_VALUE);
  const serviceId = strategyList?.[0]?.strategyDetail?.serviceId ?? FX;
  const orderableMargin = useSummaryInfoByServiceId(serviceId)?.orderableMargin ?? 0;

  const calculatedMaxValue = useMemo(() => {
    if (!orderableMargin || !minMarginRequired) return 0;
    return new Decimal(orderableMargin).div(minMarginRequired).floor().toNumber();
  }, [minMarginRequired, orderableMargin]);

  const validateInput = useCallback(
    (value) => {
      if (value === '') {
        changeErrorMessages([{ inputName: INPUT_NAME, errorMessage: '*必須項目です' }]);
        return;
      }
      if (calculatedMaxValue <= 0) {
        changeErrorMessages([
          { inputName: INPUT_NAME, errorMessage: '発注可能証拠金が不足しているため稼働できません' },
        ]);
      } else if (MIN_COUNT_VALUE <= value && value <= calculatedMaxValue) {
        changeErrorMessages([]);
      } else {
        changeErrorMessages([
          { inputName: INPUT_NAME, errorMessage: `セット数は${calculatedMaxValue}までご設定ください` },
        ]);
      }
    },
    [calculatedMaxValue],
  );
  useEffect(() => {
    if (isOpen) {
      hasSubmittedRef.current = false;
      changeErrorMessages([]);
      const initialCount = sets ?? MIN_COUNT_VALUE;
      setCount(initialCount);
      validateInput(initialCount);
    }
  }, [isOpen, sets, validateInput]);

  const setMessages = useMemo(() => {
    const formattedMaxValue = new Intl.NumberFormat('ja-JP').format(calculatedMaxValue);
    return {
      adjustNote: '資金に合わせて、セット数を調整できます。',
      maxLimitNote: `最大可能セット数: ${formattedMaxValue}セット`,
      autoTradeStartNote: '開始ボタンを押すと自動売買を開始します',
    };
  }, [calculatedMaxValue]);

  return useMemo(
    () => ({
      modalTitle: '確認画面',
      errorMessages,
      setMessages,
      countInput: {
        get: countValue,
        set: setCount,
        label: 'セット数',
        info: 'セット数を変更すると、取引数量も変更されます。\n例）：１セット=0.2万通貨、2セット=0.4万通貨。',
        name: INPUT_NAME,
        validateFunction: validateInput,
      },
      totalMarginRecommended: {
        get: totalMarginRecommended,
        label: RECOMMENDED_MARGIN_LABEL,
        tooltip: MARGIN_TOOLTIP,
      },
      totalMarginRecommendedFX: {
        get: totalMarginRecommendedFX,
      },
      totalMarginRecommendedETF: {
        get: totalMarginRecommendedETF,
      },
      totalMarginRecommendedCFD: {
        get: totalMarginRecommendedCFD,
      },
      totalMarginRequired: {
        get: totalMarginRequired,
        label: REQUIRED_MARGIN_LABEL,
        tooltip: REQUIRED_MARGIN_TOOLTIP_CONFIRM,
      },
      totalMarginRequiredFX: {
        get: totalMarginRequiredFX,
      },
      totalMarginRequiredETF: {
        get: totalMarginRequiredETF,
      },
      totalMarginRequiredCFD: {
        get: totalMarginRequiredCFD,
      },
      cancelButton: {
        label: '戻る',
        handler: closeModalAndRefreshState,
      },
      submitButton: {
        label: '自動売買を開始',
        isDisabled: isDisableConfirmButton,
        handler: onConfirm,
      },
    }),
    [
      closeModalAndRefreshState,
      countValue,
      errorMessages,
      setMessages,
      isDisableConfirmButton,
      onConfirm,
      totalMarginRecommended,
      totalMarginRecommendedFX,
      totalMarginRecommendedETF,
      totalMarginRecommendedCFD,
      totalMarginRequired,
      totalMarginRequiredFX,
      totalMarginRequiredETF,
      totalMarginRequiredCFD,
      validateInput,
    ],
  );
};

export default useAutoSelectRunNowConfirmation;
